import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  getLrsAssetTypeMeta,
  inferLrsGrade,
  LRS_CONDITIONS,
  LRS_LIFECYCLE_STAGES,
  LRS_UTILITY_TYPES,
  specFieldsForAsset,
  type LrsCondition,
  type LrsGrade,
  type LrsLifecycleStatus,
  type LrsSpecField,
} from 'src/app/shared/constants/lrs-asset-types';
import {
  LrsAssetDetail,
  LrsAssetDocument,
  LrsEvent,
  LrsService,
  LrsUtilityAsset,
} from 'src/app/shared/services/lrs.service';
import { LrsDashboardService } from 'src/app/shared/services/lrs-dashboard.service';
import { Subscription } from 'rxjs';
import { lrsIconPath } from 'src/app/shared/utils/lrs-asset-symbology';
import { parseChainageMeters } from 'src/app/shared/utils/chainage.util';

@Component({
  selector: 'app-lrs-asset-details',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './lrs-asset-details.component.html',
  styleUrls: ['./lrs-asset-details.component.css'],
})
export class LrsAssetDetailsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() kind: 'event' | 'utility' = 'event';
  @Input() assetId: number | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<LrsEvent | LrsUtilityAsset>();
  @Output() retired = new EventEmitter<void>();
  @Output() openLinked = new EventEmitter<{ kind: 'event' | 'utility'; id: number }>();

  @ViewChild('photoInput') photoInput?: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  readonly stages = LRS_LIFECYCLE_STAGES;
  readonly conditions = LRS_CONDITIONS;
  readonly relations = [
    { id: 'contains', label: 'Contains' },
    { id: 'crosses', label: 'Crosses' },
    { id: 'serves', label: 'Serves' },
    { id: 'adjacent', label: 'Adjacent' },
  ];

  tab: 'overview' | 'specification' | 'lifecycle' | 'documents' | 'history' = 'specification';
  loading = false;
  saving = false;
  editing = false;
  error: string | null = null;
  moreOpen = false;
  addLinkOpen = false;
  detail: LrsAssetDetail | null = null;
  featuredImageIndex = 0;

  edit: Record<string, unknown> = {};
  linkKind: 'event' | 'utility' = 'event';
  linkId: number | null = null;
  linkRelation = 'contains';
  private mediaSub?: Subscription;

  constructor(private lrs: LrsService, private dashboard: LrsDashboardService) {}

  ngOnInit(): void {
    this.mediaSub = this.dashboard.mediaRequest$.subscribe((kind) => {
      if (!this.assetId) return;
      if (kind === 'photo') this.pickPhoto();
      else this.pickFile();
    });
  }

  ngOnDestroy(): void {
    this.mediaSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kind'] || changes['assetId']) {
      this.tab = 'specification';
      this.editing = false;
      this.moreOpen = false;
      this.load();
    }
  }

  get asset(): (LrsEvent & LrsUtilityAsset) | null {
    return (this.detail?.asset as LrsEvent & LrsUtilityAsset) || null;
  }

  get typeId(): string {
    const a = this.asset;
    if (!a) return '';
    return this.kind === 'utility' ? a.utility_type || '' : a.event_type || '';
  }

  get typeLabel(): string {
    if (this.kind === 'utility') {
      return LRS_UTILITY_TYPES.find((u) => u.id === this.typeId)?.label || this.typeId || 'Utility';
    }
    return getLrsAssetTypeMeta(this.typeId)?.label || this.typeId || 'Asset';
  }

  get iconPath(): string {
    return getLrsAssetTypeMeta(this.typeId)?.iconPath || lrsIconPath(this.typeId || 'sign');
  }

  get grade(): LrsGrade {
    return inferLrsGrade(this.asset || {});
  }

  get gradeLabel(): string {
    const g = this.grade;
    if (this.kind === 'utility' && g === 'underground') return 'Underground Utility';
    if (g === 'underground') return 'Underground';
    if (g === 'crossover') return 'Crossover';
    if (g === 'at_grade') return 'At grade';
    return 'Overground';
  }

  get assetCode(): string {
    const a = this.asset;
    if (!a) return '';
    const prefix = (this.typeId || 'AS').slice(0, 2).toUpperCase();
    const ch = this.chainageCode(Number(a.measure_m) || 0);
    const g = this.grade === 'underground' ? 'UG' : this.grade === 'crossover' ? 'XO' : 'OG';
    return `${prefix}-${ch}-${g}`;
  }

  get mapStatus(): string {
    return this.detail?.map_status || this.asset?.map_status || 'Operational';
  }

  get conditionLabel(): string {
    const c = String(this.asset?.condition || '');
    return this.conditions.find((x) => x.id === c)?.label || c || '—';
  }

  get specFields(): LrsSpecField[] {
    return specFieldsForAsset({
      kind: this.kind,
      typeId: this.typeId,
      grade: this.grade,
    });
  }

  get images(): LrsAssetDocument[] {
    return this.detail?.images || [];
  }

  get files(): LrsAssetDocument[] {
    return this.detail?.files || [];
  }

  get featuredImage(): LrsAssetDocument | null {
    return this.images[this.featuredImageIndex] || this.images[0] || null;
  }

  load(): void {
    if (!this.assetId) {
      this.detail = null;
      return;
    }
    this.loading = true;
    this.error = null;
    this.lrs.getAssetDetail(this.kind, this.assetId).subscribe({
      next: (res) => {
        this.detail = res.data;
        this.featuredImageIndex = 0;
        this.syncEditFromAsset();
        this.loading = false;
      },
      error: (err: unknown) => {
        this.error = this.apiError(err, 'Failed to load asset');
        this.loading = false;
      },
    });
  }

  startEdit(): void {
    this.editing = true;
    this.tab = 'specification';
    this.syncEditFromAsset();
  }

  cancelEdit(): void {
    this.editing = false;
    this.syncEditFromAsset();
  }

  save(): void {
    if (!this.assetId || this.saving) return;
    const startM = this.parseChainageField(this.edit['start_chainage']);
    const endM = this.parseChainageField(this.edit['end_chainage']);
    if (this.edit['start_chainage'] && startM == null) {
      this.error = 'Start chainage must be km+mmm (for example 0+040).';
      return;
    }
    if (this.edit['end_chainage'] && endM == null) {
      this.error = 'End chainage must be km+mmm (for example 3+855).';
      return;
    }
    if (startM != null && endM != null && endM < startM) {
      this.error = 'End chainage must be greater than or equal to start chainage.';
      return;
    }
    this.saving = true;
    this.error = null;
    const body = this.buildSaveBody();
    const onNext = (res: { success: boolean; data: LrsEvent | LrsUtilityAsset }) => {
      this.saving = false;
      this.editing = false;
      this.saved.emit(res.data);
      this.load();
    };
    const onError = (err: unknown) => {
      this.saving = false;
      this.error = this.apiError(err, 'Save failed');
    };
    if (this.kind === 'utility') {
      this.lrs.updateUtilityAsset(this.assetId, body).subscribe({ next: onNext, error: onError });
    } else {
      this.lrs.updateEvent(this.assetId, body as Parameters<LrsService['updateEvent']>[1]).subscribe({
        next: onNext,
        error: onError,
      });
    }
  }

  retire(): void {
    if (!this.assetId) return;
    this.moreOpen = false;
    const onNext = () => {
      this.retired.emit();
      this.load();
    };
    const onError = (err: unknown) => (this.error = this.apiError(err, 'Retire failed'));
    if (this.kind === 'utility') {
      this.lrs.retireUtilityAsset(this.assetId).subscribe({ next: onNext, error: onError });
    } else {
      this.lrs.retireEvent(this.assetId).subscribe({ next: onNext, error: onError });
    }
  }

  pickPhoto(): void {
    this.photoInput?.nativeElement.click();
  }

  pickFile(): void {
    this.fileInput?.nativeElement.click();
  }

  onPhotoSelected(ev: Event): void {
    const file = (ev.target as HTMLInputElement).files?.[0];
    (ev.target as HTMLInputElement).value = '';
    if (!file || !this.assetId) return;
    this.lrs.uploadAssetDocument(this.kind, this.assetId, file, 'photograph').subscribe({
      next: () => this.load(),
      error: (err: unknown) => (this.error = this.apiError(err, 'Upload failed')),
    });
  }

  onFileSelected(ev: Event): void {
    const file = (ev.target as HTMLInputElement).files?.[0];
    (ev.target as HTMLInputElement).value = '';
    if (!file || !this.assetId) return;
    const type = file.type.startsWith('image/') ? 'photograph' : 'drawing';
    this.lrs.uploadAssetDocument(this.kind, this.assetId, file, type).subscribe({
      next: () => this.load(),
      error: (err: unknown) => (this.error = this.apiError(err, 'Upload failed')),
    });
  }

  addLink(): void {
    if (!this.assetId || !this.linkId) return;
    this.lrs
      .createAssetLink(this.kind, this.assetId, {
        to_kind: this.linkKind,
        to_id: Number(this.linkId),
        relation: this.linkRelation,
      })
      .subscribe({
        next: () => {
          this.addLinkOpen = false;
          this.linkId = null;
          this.load();
        },
        error: (err: unknown) => (this.error = this.apiError(err, 'Link failed')),
      });
  }

  openOther(kind?: string, id?: number): void {
    if (!kind || !id) return;
    const k = kind === 'utility' ? 'utility' : 'event';
    this.openLinked.emit({ kind: k, id });
  }

  private apiError(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const msg = (err.error as { error?: string } | null)?.error;
      return msg || fallback;
    }
    return fallback;
  }

  stageIndex(status?: string): number {
    return this.stages.findIndex((s) => s.id === status);
  }

  formatChainage(m?: number | null): string {
    if (m == null || !Number.isFinite(Number(m))) return '—';
    return this.chainageCode(Number(m)) + ' m';
  }

  formatDate(value?: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  formatBytes(n?: number | null): string {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  offsetSide(offset?: number | null): string {
    const n = Number(offset);
    if (!Number.isFinite(n) || n === 0) return 'Center';
    return n < 0 ? 'Left' : 'Right';
  }

  setLifecycle(id: LrsLifecycleStatus): void {
    this.edit['lifecycle_status'] = id;
  }

  setCondition(id: LrsCondition): void {
    this.edit['condition'] = id;
  }

  private chainageCode(m: number): string {
    const km = Math.floor(m / 1000);
    const rest = Math.round(m - km * 1000);
    return `${km}+${String(rest).padStart(3, '0')}`;
  }

  private syncEditFromAsset(): void {
    const a = this.asset;
    if (!a) {
      this.edit = {};
      return;
    }
    const props = (a.properties || {}) as Record<string, unknown>;
    this.edit = {
      label: a.label || '',
      lifecycle_status: a.lifecycle_status || 'operational',
      condition: a.condition || '',
      owner_name: a.owner_name || '',
      material: a.material || '',
      diameter_mm: a.diameter_mm ?? '',
      depth_m: a.depth_m ?? '',
      side: a.side || '',
      median_offset_m: a.offset_m ?? a.lateral_offset_m ?? '',
      edge_offset_m: a.edge_offset_m ?? '',
      installation_date: this.isoDate(a.installation_date),
      commissioning_date: this.isoDate(a.commissioning_date),
      last_inspection_date: this.isoDate(a.last_inspection_date),
      next_inspection_date: this.isoDate(a.next_inspection_date),
      replacement_date: this.isoDate(a.replacement_date),
      decommission_date: this.isoDate(a.decommission_date),
      expected_life_years: a.expected_life_years ?? '',
      start_chainage: this.chainageCode(Number(a.measure_m) || 0),
      end_chainage: this.chainageCode(Number(a.measure_to_m ?? a.measure_m) || 0),
    };
    for (const field of this.specFields) {
      if (field.store !== 'properties') continue;
      const v = props[field.key];
      this.edit[field.key] = v != null ? v : '';
    }
  }

  private isoDate(v?: string | null): string {
    if (!v) return '';
    return String(v).slice(0, 10);
  }

  specValue(field: LrsSpecField): unknown {
    const a = this.asset;
    if (!a) return '';
    if (field.key === 'type') return this.typeLabel;
    if (field.key === 'start_chainage') return this.formatChainage(a.measure_m);
    if (field.key === 'end_chainage') return this.formatChainage(a.measure_to_m || a.measure_m);
    if (field.key === 'median_offset_m') {
      const n = a.offset_m ?? a.lateral_offset_m;
      if (n == null) return '—';
      return `${Number(n).toFixed(2)} m (${this.offsetSide(Number(n))})`;
    }
    if (field.key === 'edge_offset_m') {
      return a.edge_offset_m != null ? `${Number(a.edge_offset_m).toFixed(2)} m` : '—';
    }
    if (field.store === 'properties') {
      const v = a.properties?.[field.key];
      return v != null && v !== '' ? v : '—';
    }
    const raw = (a as unknown as Record<string, unknown>)[field.key];
    if (field.kind === 'date') return this.formatDate(raw as string);
    if (field.key === 'condition') return this.conditionLabel;
    if (field.key === 'lifecycle_status') {
      return this.stages.find((s) => s.id === raw)?.label || raw || '—';
    }
    if (raw == null || raw === '') return '—';
    return field.unit ? `${raw} ${field.unit}` : raw;
  }

  private buildSaveBody(): Record<string, unknown> {
    const e = this.edit;
    const num = (k: string) => {
      const v = e[k];
      if (v === '' || v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const properties: Record<string, unknown> = { ...(this.asset?.properties || {}) };
    for (const field of this.specFields) {
      if (field.store !== 'properties') continue;
      const v = e[field.key];
      properties[field.key] = v === '' ? null : v;
    }
    const median = num('median_offset_m');
    const body: Record<string, unknown> = {
      label: e['label'],
      lifecycle_status: e['lifecycle_status'] || 'operational',
      condition: e['condition'] || null,
      owner_name: e['owner_name'] || null,
      material: e['material'] || null,
      diameter_mm: num('diameter_mm'),
      depth_m: num('depth_m'),
      side: e['side'] || null,
      edge_offset_m: num('edge_offset_m'),
      installation_date: e['installation_date'] || null,
      commissioning_date: e['commissioning_date'] || null,
      last_inspection_date: e['last_inspection_date'] || null,
      next_inspection_date: e['next_inspection_date'] || null,
      replacement_date: e['replacement_date'] || null,
      decommission_date: e['decommission_date'] || null,
      expected_life_years: num('expected_life_years'),
      properties,
    };
    if (this.kind === 'utility') body['lateral_offset_m'] = median;
    else body['offset_m'] = median;

    const startM = this.parseChainageField(e['start_chainage']);
    const endM = this.parseChainageField(e['end_chainage']);
    if (startM != null) body['measure_m'] = startM;
    if (endM != null) body['measure_to_m'] = endM;
    return body;
  }

  private parseChainageField(value: unknown): number | null {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    return parseChainageMeters(String(value));
  }
}
